import React, { useState, useCallback } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Check, Loader2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import TurnstileWidget from '../components/TurnstileWidget';
import { useToast } from '../hooks/use-toast';
import { api, formatApiError } from '../lib/api';
import { SITE } from '../data/mock';

export default function Contact() {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [tsToken, setTsToken] = useState(null);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onVerify = useCallback((token) => setTsToken(token), []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Required fields missing', description: 'Please add at least your name and email.' });
      return;
    }
    if (!tsToken) {
      toast({ title: 'Verification required', description: 'Please complete the "I\'m not a robot" check.' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/public/contact', { ...form, turnstile_token: tsToken });
      setSubmitted(true);
      toast({ title: 'Message sent', description: 'Our team will be in touch within 24 hours.' });
    } catch (err) {
      toast({ title: 'Submission failed', description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Get in touch"
        title="Let’s talk about your business"
        subtitle="Tell us what you need — our specialists will craft a tailored proposal and call you back within 24 hours."
        breadcrumb="Contact"
      />

      <section className="bg-white">
        <div className="container-x section-pad grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <div className="card-modern">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
                  <Phone size={18} />
                </span>
                <p className="font-bold text-slate-900">Call us</p>
              </div>
              <a href={`tel:${SITE.phoneTel}`} className="text-violet-600 font-semibold">
                {SITE.phone}
              </a>
              <p className="text-xs text-slate-500 mt-1">{SITE.hours}</p>
            </div>

            <div className="card-modern">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
                  <Mail size={18} />
                </span>
                <p className="font-bold text-slate-900">Email</p>
              </div>
              <a href={`mailto:${SITE.email}`} className="text-violet-600 font-semibold">
                {SITE.email}
              </a>
              <p className="text-xs text-slate-500 mt-1">We reply within 1 business day.</p>
            </div>

            <div className="card-modern">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
                  <MapPin size={18} />
                </span>
                <p className="font-bold text-slate-900">London office</p>
              </div>
              <p className="text-slate-700 text-sm">{SITE.address}</p>
              <iframe
                title="VoIPinger London office"
                src="https://www.google.com/maps?q=60%20Copthorne%20Avenue%20Ilford%20IG6%202SQ&output=embed"
                className="mt-4 w-full h-48 rounded-xl border border-slate-200"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-xl shadow-slate-900/5">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 rounded-full bg-violet-50 text-violet-600 mx-auto mb-4 flex items-center justify-center">
                    <Check size={28} />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Thanks {form.name.split(' ')[0]} — message received</h3>
                  <p className="text-slate-600">A specialist will reach out shortly. In the meantime, feel free to call us directly.</p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', company: '', message: '' }); }}
                    className="btn-light mt-6"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="eyebrow">Get a free consultation</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input label="Name *" name="name" value={form.name} onChange={handle} placeholder="Jane Smith" />
                    <Input label="Email *" name="email" type="email" value={form.email} onChange={handle} placeholder="jane@company.com" />
                    <Input label="Phone Number" name="phone" value={form.phone} onChange={handle} placeholder="+44 ..." />
                    <Input label="Company Name" name="company" value={form.company} onChange={handle} placeholder="Acme Ltd" />
                  </div>
                  <label className="block">
                    <span className="text-sm font-semibold text-slate-700 mb-1.5 block">Anything else?</span>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handle}
                      rows={5}
                      placeholder="Tell us about your project, team size and goals..."
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 outline-none resize-none"
                    />
                  </label>
                  <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="contact-submit">
                    {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <>Send <Send size={16} /></>}
                  </button>
                  <div className="flex justify-center pt-2">
                    <TurnstileWidget onVerify={onVerify} />
                  </div>
                  <p className="text-xs text-slate-500 text-center">By sending you agree to our terms. We never share your data.</p>
                </form>
              )}
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              {[
                { t: 'Next-day', s: 'setup' },
                { t: '14 days', s: 'free trial' },
                { t: 'Pro', s: 'support team' }
              ].map((b) => (
                <div key={b.t} className="bg-slate-50 border border-slate-200 rounded-xl py-4">
                  <p className="font-bold text-slate-900">{b.t}</p>
                  <p className="text-xs text-slate-500">{b.s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Input({ label, ...rest }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700 mb-1.5 block">{label}</span>
      <input
        {...rest}
        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 outline-none"
      />
    </label>
  );
}
